import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["type", "name", "modifier", "elementTitle", "imageData", "onMount", "onMountProps", "parent", "mapShapes", "centerCoordinates"];
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/router';
import { withTitle } from '@blaze-cms/react-page-builder';
import { useInView } from '@blaze-react/utils/lib/customHooks';
import { functions, isEqual, omit } from 'lodash';
import { setMapOptions, addZoomListener } from './helpers';
import { GOOGLE_MAPS_SCRIPT, LOAD, SCRIPT, IN_VIEW_CONFIG } from '../constants';
import GoogleMapsHtml from './GoogleMapsHtml';
const shouldNotUpdate = (props, nextProps) => {
  const [funcs, nextFuncs] = [functions(props), functions(nextProps)];
  const noPropChange = isEqual(omit(props, funcs), omit(nextProps, nextFuncs));
  const noFuncChange = funcs.length === nextFuncs.length && funcs.every(fn => props[fn].toString() === nextProps[fn].toString());
  return noPropChange && noFuncChange;
};
const GoogleMaps = _ref => {
  let {
      type,
      name,
      modifier,
      elementTitle,
      imageData,
      onMount,
      onMountProps,
      parent,
      mapShapes,
      centerCoordinates
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  const router = useRouter();
  const [isIntersecting, mapRef] = useInView(IN_VIEW_CONFIG);
  const client = useApolloClient();
  const [map, setMap] = useState();
  useEffect(() => {
    const options = setMapOptions(otherProps);
    const onLoad = () => {
      const initialMap = new window.google.maps.Map(mapRef.current, options);
      if (centerCoordinates.length === 1) {
        const [{
          latitude: lat,
          longitude: lng
        }] = centerCoordinates;
        initialMap.setCenter({
          lat,
          lng
        });
      } else {
        const bounds = new window.google.maps.LatLngBounds();
        centerCoordinates.forEach(({
          latitude,
          longitude
        }) => {
          bounds.extend(new window.google.maps.LatLng(latitude, longitude));
        });
        initialMap.fitBounds(bounds);
      }
      setMap(initialMap);
    };
    if (!window.google) {
      const script = document.createElement(SCRIPT);
      script.src = `${GOOGLE_MAPS_SCRIPT}${process.env.GOOGLE_MAPS_API_KEY}`;
      document.head.append(script);
      script.addEventListener(LOAD, onLoad);
      return () => script.removeEventListener(LOAD, onLoad);
    }
    onLoad();
    if (map) {
      map.setOptions(options);
      const {
        enableGeolocation
      } = otherProps;
      const shouldActivateGeolocation = enableGeolocation && navigator.geolocation;
      if (shouldActivateGeolocation) {
        navigator.geolocation.getCurrentPosition(position => {
          const center = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          map.setCenter(center);
        }, () => {});
      }
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (map && typeof onMount === `function`) onMount(map, onMountProps);
  if (isIntersecting) addZoomListener(map, router, mapShapes, imageData, client);
  return /*#__PURE__*/React.createElement(GoogleMapsHtml, {
    mapRef: mapRef
  });
};
GoogleMaps.propTypes = {
  mapShapes: PropTypes.array,
  imageData: PropTypes.array,
  type: PropTypes.string,
  name: PropTypes.string,
  modifier: PropTypes.string,
  elementTitle: PropTypes.string,
  onMount: PropTypes.func,
  onMountProps: PropTypes.object,
  parent: PropTypes.object,
  centerCoordinates: PropTypes.array
};
GoogleMaps.defaultProps = {
  mapShapes: [],
  imageData: [],
  centerCoordinates: [],
  type: '',
  name: '',
  modifier: '',
  elementTitle: '',
  onMount: null,
  onMountProps: null,
  parent: {}
};
export default React.memo(withTitle(GoogleMaps), shouldNotUpdate);
//# sourceMappingURL=GoogleMaps.js.map